import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, unref as _unref, Fragment as _Fragment, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "images-wrapper" }
const _hoisted_2 = { class: "image-panel" }
const _hoisted_3 = {
  key: 0,
  class: "panel-title"
}
const _hoisted_4 = { class: "panel-label" }
const _hoisted_5 = ["src", "alt"]
const _hoisted_6 = { class: "image-controls" }
const _hoisted_7 = ["disabled", "title"]
const _hoisted_8 = { class: "zoom-level" }
const _hoisted_9 = ["disabled", "title"]
const _hoisted_10 = ["title"]
const _hoisted_11 = { class: "image-panel" }
const _hoisted_12 = {
  key: 0,
  class: "panel-title"
}
const _hoisted_13 = { class: "panel-label" }
const _hoisted_14 = ["src", "alt"]
const _hoisted_15 = { class: "image-controls" }
const _hoisted_16 = ["disabled", "title"]
const _hoisted_17 = { class: "zoom-level" }
const _hoisted_18 = ["disabled", "title"]
const _hoisted_19 = ["title"]
const _hoisted_20 = { class: "modal-footer" }
const _hoisted_21 = { class: "footer-left" }
const _hoisted_22 = {
  key: 0,
  class: "sync-toggles"
}
const _hoisted_23 = { class: "sync-toggle" }
const _hoisted_24 = { class: "sync-label" }
const _hoisted_25 = { class: "sync-toggle" }
const _hoisted_26 = { class: "sync-label" }
const _hoisted_27 = { class: "footer-center" }
const _hoisted_28 = { class: "keyboard-hints" }
const _hoisted_29 = { class: "hint" }
const _hoisted_30 = { class: "hint" }
const _hoisted_31 = { class: "hint" }
const _hoisted_32 = { class: "hint" }
const _hoisted_33 = { class: "hint" }
const _hoisted_34 = { class: "footer-right" }
const _hoisted_35 = ["title"]
const _hoisted_36 = { class: "close-label" }

import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

interface ImageData {
  url: string;
  alt: string;
  label?: string;
}

interface Props {
  visible: boolean;
  image?: ImageData | null;
  comparisonImage?: ImageData | null;
  minZoom?: number;
  maxZoom?: number;
  zoomStep?: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'imageViewerModal',
  props: {
    visible: { type: Boolean, default: false },
    image: { default: null },
    comparisonImage: { default: null },
    minZoom: { default: 0.5 },
    maxZoom: { default: 5 },
    zoomStep: { default: 0.25 }
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const { t } = useI18n();

// Types
const props = __props;

const emit = __emit;

// State
const zoom = ref(1);
const panX = ref(0);
const panY = ref(0);
const isPanning = ref(false);
const panStart = ref({ x: 0, y: 0 });
const lastPan = ref({ x: 0, y: 0 });

// Comparison mode state
const comparisonZoom = ref(1);
const comparisonPanX = ref(0);
const comparisonPanY = ref(0);
const isComparisonPanning = ref(false);
const comparisonPanStart = ref({ x: 0, y: 0 });
const comparisonLastPan = ref({ x: 0, y: 0 });
const syncZoom = ref(true);
const syncPan = ref(true);

// Touch state
const lastTouchDistance = ref(0);
const isTouchZooming = ref(false);

// Refs
const containerRef = ref<HTMLElement | null>(null);
const imageRef = ref<HTMLImageElement | null>(null);
const comparisonImageRef = ref<HTMLImageElement | null>(null);
const modalRef = ref<HTMLElement | null>(null);

// Computed
const isComparisonMode = computed(() => !!props.comparisonImage?.url);

const zoomPercentage = computed(() => Math.round(zoom.value * 100));
const comparisonZoomPercentage = computed(() => Math.round(comparisonZoom.value * 100));

const imageStyle = computed(() => ({
  transform: `translate(${panX.value}px, ${panY.value}px) scale(${zoom.value})`,
  cursor: isPanning.value ? 'grabbing' : 'grab',
}));

const comparisonImageStyle = computed(() => ({
  transform: `translate(${comparisonPanX.value}px, ${comparisonPanY.value}px) scale(${comparisonZoom.value})`,
  cursor: isComparisonPanning.value ? 'grabbing' : 'grab',
}));

// Methods
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function setZoom(newZoom: number, isComparison = false) {
  const clampedZoom = clamp(newZoom, props.minZoom, props.maxZoom);
  
  if (isComparison && !syncZoom.value) {
    comparisonZoom.value = clampedZoom;
  } else if (syncZoom.value && isComparisonMode.value) {
    zoom.value = clampedZoom;
    comparisonZoom.value = clampedZoom;
  } else {
    zoom.value = clampedZoom;
  }
}

function setPan(newX: number, newY: number, isComparison = false) {
  if (isComparison && !syncPan.value) {
    comparisonPanX.value = newX;
    comparisonPanY.value = newY;
  } else if (syncPan.value && isComparisonMode.value) {
    panX.value = newX;
    panY.value = newY;
    comparisonPanX.value = newX;
    comparisonPanY.value = newY;
  } else {
    if (isComparison) {
      comparisonPanX.value = newX;
      comparisonPanY.value = newY;
    } else {
      panX.value = newX;
      panY.value = newY;
    }
  }
}

function zoomIn(isComparison = false) {
  const currentZoom = isComparison && !syncZoom.value ? comparisonZoom.value : zoom.value;
  setZoom(currentZoom + props.zoomStep, isComparison);
}

function zoomOut(isComparison = false) {
  const currentZoom = isComparison && !syncZoom.value ? comparisonZoom.value : zoom.value;
  setZoom(currentZoom - props.zoomStep, isComparison);
}

function resetView(isComparison = false) {
  if (isComparison && !syncZoom.value && !syncPan.value) {
    comparisonZoom.value = 1;
    comparisonPanX.value = 0;
    comparisonPanY.value = 0;
  } else if ((syncZoom.value || syncPan.value) && isComparisonMode.value) {
    zoom.value = 1;
    comparisonZoom.value = 1;
    panX.value = 0;
    panY.value = 0;
    comparisonPanX.value = 0;
    comparisonPanY.value = 0;
  } else {
    zoom.value = 1;
    panX.value = 0;
    panY.value = 0;
  }
}

function resetAll() {
  zoom.value = 1;
  comparisonZoom.value = 1;
  panX.value = 0;
  panY.value = 0;
  comparisonPanX.value = 0;
  comparisonPanY.value = 0;
}

function close() {
  emit('close');
}

// Mouse wheel zoom
function handleWheel(event: WheelEvent, isComparison = false) {
  event.preventDefault();
  const delta = event.deltaY > 0 ? -props.zoomStep : props.zoomStep;
  const currentZoom = isComparison && !syncZoom.value ? comparisonZoom.value : zoom.value;
  setZoom(currentZoom + delta, isComparison);
}

// Pan handlers
function startPan(event: MouseEvent, isComparison = false) {
  if (event.button !== 0) return; // Only left click
  
  event.preventDefault();
  
  if (isComparison) {
    isComparisonPanning.value = true;
    comparisonPanStart.value = { x: event.clientX, y: event.clientY };
    comparisonLastPan.value = { x: comparisonPanX.value, y: comparisonPanY.value };
    // Also store primary pan start for sync mode
    if (syncPan.value && isComparisonMode.value) {
      lastPan.value = { x: panX.value, y: panY.value };
    }
  } else {
    isPanning.value = true;
    panStart.value = { x: event.clientX, y: event.clientY };
    lastPan.value = { x: panX.value, y: panY.value };
    // Also store comparison pan start for sync mode
    if (syncPan.value && isComparisonMode.value) {
      comparisonLastPan.value = { x: comparisonPanX.value, y: comparisonPanY.value };
    }
  }
}

function onPan(event: MouseEvent) {
  if (isPanning.value) {
    const deltaX = event.clientX - panStart.value.x;
    const deltaY = event.clientY - panStart.value.y;
    const newX = lastPan.value.x + deltaX;
    const newY = lastPan.value.y + deltaY;
    
    if (syncPan.value && isComparisonMode.value) {
      panX.value = newX;
      panY.value = newY;
      comparisonPanX.value = comparisonLastPan.value.x + deltaX;
      comparisonPanY.value = comparisonLastPan.value.y + deltaY;
    } else {
      panX.value = newX;
      panY.value = newY;
    }
  }
  
  if (isComparisonPanning.value) {
    const deltaX = event.clientX - comparisonPanStart.value.x;
    const deltaY = event.clientY - comparisonPanStart.value.y;
    const newX = comparisonLastPan.value.x + deltaX;
    const newY = comparisonLastPan.value.y + deltaY;
    
    if (syncPan.value && isComparisonMode.value) {
      comparisonPanX.value = newX;
      comparisonPanY.value = newY;
      panX.value = lastPan.value.x + deltaX;
      panY.value = lastPan.value.y + deltaY;
    } else {
      comparisonPanX.value = newX;
      comparisonPanY.value = newY;
    }
  }
}

function endPan() {
  isPanning.value = false;
  isComparisonPanning.value = false;
}

// Touch handlers
function getTouchDistance(touches: TouchList): number {
  if (touches.length < 2) return 0;
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

function handleTouchStart(event: TouchEvent, isComparison = false) {
  if (event.touches.length === 2) {
    // Pinch zoom start
    event.preventDefault();
    isTouchZooming.value = true;
    lastTouchDistance.value = getTouchDistance(event.touches);
  } else if (event.touches.length === 1) {
    // Pan start
    const touch = event.touches[0];
    if (isComparison) {
      isComparisonPanning.value = true;
      comparisonPanStart.value = { x: touch.clientX, y: touch.clientY };
      comparisonLastPan.value = { x: comparisonPanX.value, y: comparisonPanY.value };
      if (syncPan.value && isComparisonMode.value) {
        lastPan.value = { x: panX.value, y: panY.value };
      }
    } else {
      isPanning.value = true;
      panStart.value = { x: touch.clientX, y: touch.clientY };
      lastPan.value = { x: panX.value, y: panY.value };
      if (syncPan.value && isComparisonMode.value) {
        comparisonLastPan.value = { x: comparisonPanX.value, y: comparisonPanY.value };
      }
    }
  }
}

function handleTouchMove(event: TouchEvent, isComparison = false) {
  if (event.touches.length === 2 && isTouchZooming.value) {
    // Pinch zoom
    event.preventDefault();
    const distance = getTouchDistance(event.touches);
    const scale = distance / lastTouchDistance.value;
    const currentZoom = isComparison && !syncZoom.value ? comparisonZoom.value : zoom.value;
    setZoom(currentZoom * scale, isComparison);
    lastTouchDistance.value = distance;
  } else if (event.touches.length === 1) {
    // Pan
    const touch = event.touches[0];
    if (isPanning.value) {
      const deltaX = touch.clientX - panStart.value.x;
      const deltaY = touch.clientY - panStart.value.y;
      
      if (syncPan.value && isComparisonMode.value) {
        panX.value = lastPan.value.x + deltaX;
        panY.value = lastPan.value.y + deltaY;
        comparisonPanX.value = comparisonLastPan.value.x + deltaX;
        comparisonPanY.value = comparisonLastPan.value.y + deltaY;
      } else {
        panX.value = lastPan.value.x + deltaX;
        panY.value = lastPan.value.y + deltaY;
      }
    }
    if (isComparisonPanning.value) {
      const deltaX = touch.clientX - comparisonPanStart.value.x;
      const deltaY = touch.clientY - comparisonPanStart.value.y;
      
      if (syncPan.value && isComparisonMode.value) {
        comparisonPanX.value = comparisonLastPan.value.x + deltaX;
        comparisonPanY.value = comparisonLastPan.value.y + deltaY;
        panX.value = lastPan.value.x + deltaX;
        panY.value = lastPan.value.y + deltaY;
      } else {
        comparisonPanX.value = comparisonLastPan.value.x + deltaX;
        comparisonPanY.value = comparisonLastPan.value.y + deltaY;
      }
    }
  }
}

function handleTouchEnd() {
  isTouchZooming.value = false;
  isPanning.value = false;
  isComparisonPanning.value = false;
}

// Keyboard handlers
function handleKeydown(event: KeyboardEvent) {
  if (!props.visible) return;
  
  switch (event.key) {
    case 'Escape':
      close();
      break;
    case '+':
    case '=':
      event.preventDefault();
      zoomIn();
      break;
    case '-':
      event.preventDefault();
      zoomOut();
      break;
    case '0':
      event.preventDefault();
      resetAll();
      break;
    case 'ArrowUp':
      event.preventDefault();
      if (syncPan.value && isComparisonMode.value) {
        panY.value += 50;
        comparisonPanY.value += 50;
      } else {
        panY.value += 50;
      }
      break;
    case 'ArrowDown':
      event.preventDefault();
      if (syncPan.value && isComparisonMode.value) {
        panY.value -= 50;
        comparisonPanY.value -= 50;
      } else {
        panY.value -= 50;
      }
      break;
    case 'ArrowLeft':
      event.preventDefault();
      if (syncPan.value && isComparisonMode.value) {
        panX.value += 50;
        comparisonPanX.value += 50;
      } else {
        panX.value += 50;
      }
      break;
    case 'ArrowRight':
      event.preventDefault();
      if (syncPan.value && isComparisonMode.value) {
        panX.value -= 50;
        comparisonPanX.value -= 50;
      } else {
        panX.value -= 50;
      }
      break;
  }
}

// Click outside to close
function handleBackdropClick(event: MouseEvent) {
  if (event.target === modalRef.value) {
    close();
  }
}

// Lifecycle
watch(() => props.visible, (visible) => {
  if (visible) {
    resetAll();
    nextTick(() => {
      modalRef.value?.focus();
    });
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
});

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
  document.addEventListener('mousemove', onPan);
  document.addEventListener('mouseup', endPan);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown);
  document.removeEventListener('mousemove', onPan);
  document.removeEventListener('mouseup', endPan);
  document.body.style.overflow = '';
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "modal" }, {
      default: _withCtx(() => [
        (_ctx.visible)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              ref_key: "modalRef",
              ref: modalRef,
              class: "image-viewer-modal",
              tabindex: "-1",
              onClick: handleBackdropClick,
              role: "dialog"
            }, [
              _createElementVNode("div", {
                class: _normalizeClass(["modal-content", { 'comparison-mode': isComparisonMode.value }])
              }, [
                _createElementVNode("div", _hoisted_1, [
                  _createElementVNode("div", _hoisted_2, [
                    (_ctx.image?.label)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                          _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.image.label), 1)
                        ]))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", {
                      ref_key: "containerRef",
                      ref: containerRef,
                      class: "image-container",
                      onWheel: _cache[0] || (_cache[0] = (e) => handleWheel(e, false)),
                      onMousedown: _cache[1] || (_cache[1] = (e) => startPan(e, false)),
                      onTouchstart: _cache[2] || (_cache[2] = (e) => handleTouchStart(e, false)),
                      onTouchmove: _cache[3] || (_cache[3] = (e) => handleTouchMove(e, false)),
                      onTouchend: handleTouchEnd
                    }, [
                      (_ctx.image?.url)
                        ? (_openBlock(), _createElementBlock("img", {
                            key: 0,
                            ref_key: "imageRef",
                            ref: imageRef,
                            src: _ctx.image.url,
                            alt: _ctx.image.alt,
                            style: _normalizeStyle(imageStyle.value),
                            class: "viewer-image",
                            draggable: "false"
                          }, null, 12, _hoisted_5))
                        : _createCommentVNode("", true)
                    ], 544),
                    _createElementVNode("div", _hoisted_6, [
                      _createElementVNode("button", {
                        class: "control-btn",
                        onClick: _cache[4] || (_cache[4] = ($event: any) => (zoomOut(false))),
                        disabled: zoom.value <= _ctx.minZoom,
                        title: _unref(t)('patient.imageViewer.zoomOut')
                      }, _cache[16] || (_cache[16] = [
                        _createElementVNode("svg", {
                          width: "20",
                          height: "20",
                          viewBox: "0 0 24 24",
                          fill: "none",
                          stroke: "currentColor",
                          "stroke-width": "2"
                        }, [
                          _createElementVNode("circle", {
                            cx: "11",
                            cy: "11",
                            r: "8"
                          }),
                          _createElementVNode("line", {
                            x1: "21",
                            y1: "21",
                            x2: "16.65",
                            y2: "16.65"
                          }),
                          _createElementVNode("line", {
                            x1: "8",
                            y1: "11",
                            x2: "14",
                            y2: "11"
                          })
                        ], -1)
                      ]), 8, _hoisted_7),
                      _createElementVNode("span", _hoisted_8, _toDisplayString(zoomPercentage.value) + "%", 1),
                      _createElementVNode("button", {
                        class: "control-btn",
                        onClick: _cache[5] || (_cache[5] = ($event: any) => (zoomIn(false))),
                        disabled: zoom.value >= _ctx.maxZoom,
                        title: _unref(t)('patient.imageViewer.zoomIn')
                      }, _cache[17] || (_cache[17] = [
                        _createElementVNode("svg", {
                          width: "20",
                          height: "20",
                          viewBox: "0 0 24 24",
                          fill: "none",
                          stroke: "currentColor",
                          "stroke-width": "2"
                        }, [
                          _createElementVNode("circle", {
                            cx: "11",
                            cy: "11",
                            r: "8"
                          }),
                          _createElementVNode("line", {
                            x1: "21",
                            y1: "21",
                            x2: "16.65",
                            y2: "16.65"
                          }),
                          _createElementVNode("line", {
                            x1: "11",
                            y1: "8",
                            x2: "11",
                            y2: "14"
                          }),
                          _createElementVNode("line", {
                            x1: "8",
                            y1: "11",
                            x2: "14",
                            y2: "11"
                          })
                        ], -1)
                      ]), 8, _hoisted_9),
                      _cache[19] || (_cache[19] = _createElementVNode("div", { class: "control-separator" }, null, -1)),
                      _createElementVNode("button", {
                        class: "control-btn",
                        onClick: _cache[6] || (_cache[6] = ($event: any) => (resetView(false))),
                        title: _unref(t)('patient.imageViewer.resetView')
                      }, _cache[18] || (_cache[18] = [
                        _createElementVNode("svg", {
                          width: "20",
                          height: "20",
                          viewBox: "0 0 24 24",
                          fill: "none",
                          stroke: "currentColor",
                          "stroke-width": "2"
                        }, [
                          _createElementVNode("path", { d: "M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" }),
                          _createElementVNode("path", { d: "M3 3v5h5" })
                        ], -1)
                      ]), 8, _hoisted_10)
                    ])
                  ]),
                  (isComparisonMode.value)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _cache[24] || (_cache[24] = _createElementVNode("div", { class: "divider" }, null, -1)),
                        _createElementVNode("div", _hoisted_11, [
                          (_ctx.comparisonImage?.label)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                _createElementVNode("span", _hoisted_13, _toDisplayString(_ctx.comparisonImage.label), 1)
                              ]))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", {
                            class: "image-container",
                            onWheel: _cache[7] || (_cache[7] = (e) => handleWheel(e, true)),
                            onMousedown: _cache[8] || (_cache[8] = (e) => startPan(e, true)),
                            onTouchstart: _cache[9] || (_cache[9] = (e) => handleTouchStart(e, true)),
                            onTouchmove: _cache[10] || (_cache[10] = (e) => handleTouchMove(e, true)),
                            onTouchend: handleTouchEnd
                          }, [
                            (_ctx.comparisonImage?.url)
                              ? (_openBlock(), _createElementBlock("img", {
                                  key: 0,
                                  ref_key: "comparisonImageRef",
                                  ref: comparisonImageRef,
                                  src: _ctx.comparisonImage.url,
                                  alt: _ctx.comparisonImage.alt,
                                  style: _normalizeStyle(comparisonImageStyle.value),
                                  class: "viewer-image",
                                  draggable: "false"
                                }, null, 12, _hoisted_14))
                              : _createCommentVNode("", true)
                          ], 32),
                          _createElementVNode("div", _hoisted_15, [
                            _createElementVNode("button", {
                              class: "control-btn",
                              onClick: _cache[11] || (_cache[11] = ($event: any) => (zoomOut(true))),
                              disabled: comparisonZoom.value <= _ctx.minZoom,
                              title: _unref(t)('patient.imageViewer.zoomOut')
                            }, _cache[20] || (_cache[20] = [
                              _createElementVNode("svg", {
                                width: "20",
                                height: "20",
                                viewBox: "0 0 24 24",
                                fill: "none",
                                stroke: "currentColor",
                                "stroke-width": "2"
                              }, [
                                _createElementVNode("circle", {
                                  cx: "11",
                                  cy: "11",
                                  r: "8"
                                }),
                                _createElementVNode("line", {
                                  x1: "21",
                                  y1: "21",
                                  x2: "16.65",
                                  y2: "16.65"
                                }),
                                _createElementVNode("line", {
                                  x1: "8",
                                  y1: "11",
                                  x2: "14",
                                  y2: "11"
                                })
                              ], -1)
                            ]), 8, _hoisted_16),
                            _createElementVNode("span", _hoisted_17, _toDisplayString(comparisonZoomPercentage.value) + "%", 1),
                            _createElementVNode("button", {
                              class: "control-btn",
                              onClick: _cache[12] || (_cache[12] = ($event: any) => (zoomIn(true))),
                              disabled: comparisonZoom.value >= _ctx.maxZoom,
                              title: _unref(t)('patient.imageViewer.zoomIn')
                            }, _cache[21] || (_cache[21] = [
                              _createElementVNode("svg", {
                                width: "20",
                                height: "20",
                                viewBox: "0 0 24 24",
                                fill: "none",
                                stroke: "currentColor",
                                "stroke-width": "2"
                              }, [
                                _createElementVNode("circle", {
                                  cx: "11",
                                  cy: "11",
                                  r: "8"
                                }),
                                _createElementVNode("line", {
                                  x1: "21",
                                  y1: "21",
                                  x2: "16.65",
                                  y2: "16.65"
                                }),
                                _createElementVNode("line", {
                                  x1: "11",
                                  y1: "8",
                                  x2: "11",
                                  y2: "14"
                                }),
                                _createElementVNode("line", {
                                  x1: "8",
                                  y1: "11",
                                  x2: "14",
                                  y2: "11"
                                })
                              ], -1)
                            ]), 8, _hoisted_18),
                            _cache[23] || (_cache[23] = _createElementVNode("div", { class: "control-separator" }, null, -1)),
                            _createElementVNode("button", {
                              class: "control-btn",
                              onClick: _cache[13] || (_cache[13] = ($event: any) => (resetView(true))),
                              title: _unref(t)('patient.imageViewer.resetView')
                            }, _cache[22] || (_cache[22] = [
                              _createElementVNode("svg", {
                                width: "20",
                                height: "20",
                                viewBox: "0 0 24 24",
                                fill: "none",
                                stroke: "currentColor",
                                "stroke-width": "2"
                              }, [
                                _createElementVNode("path", { d: "M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" }),
                                _createElementVNode("path", { d: "M3 3v5h5" })
                              ], -1)
                            ]), 8, _hoisted_19)
                          ])
                        ])
                      ], 64))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_20, [
                  _createElementVNode("div", _hoisted_21, [
                    (isComparisonMode.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                          _createElementVNode("label", _hoisted_23, [
                            _withDirectives(_createElementVNode("input", {
                              type: "checkbox",
                              "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((syncZoom).value = $event))
                            }, null, 512), [
                              [_vModelCheckbox, syncZoom.value]
                            ]),
                            _createElementVNode("span", _hoisted_24, _toDisplayString(_unref(t)('patient.imageViewer.syncZoom')), 1)
                          ]),
                          _createElementVNode("label", _hoisted_25, [
                            _withDirectives(_createElementVNode("input", {
                              type: "checkbox",
                              "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((syncPan).value = $event))
                            }, null, 512), [
                              [_vModelCheckbox, syncPan.value]
                            ]),
                            _createElementVNode("span", _hoisted_26, _toDisplayString(_unref(t)('patient.imageViewer.syncPan')), 1)
                          ])
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("div", _hoisted_27, [
                    _createElementVNode("div", _hoisted_28, [
                      _createElementVNode("span", _hoisted_29, [
                        _createElementVNode("kbd", null, _toDisplayString(_unref(t)('patient.imageViewer.scroll')), 1),
                        _createTextVNode(" " + _toDisplayString(_unref(t)('patient.imageViewer.zoom')), 1)
                      ]),
                      _createElementVNode("span", _hoisted_30, [
                        _cache[25] || (_cache[25] = _createElementVNode("kbd", null, "+", -1)),
                        _cache[26] || (_cache[26] = _createElementVNode("kbd", null, "-", -1)),
                        _createTextVNode(" " + _toDisplayString(_unref(t)('patient.imageViewer.zoomInOut')), 1)
                      ]),
                      _createElementVNode("span", _hoisted_31, [
                        _cache[27] || (_cache[27] = _createElementVNode("kbd", null, "0", -1)),
                        _createTextVNode(" " + _toDisplayString(_unref(t)('patient.imageViewer.reset')), 1)
                      ]),
                      _createElementVNode("span", _hoisted_32, [
                        _cache[28] || (_cache[28] = _createElementVNode("kbd", null, "↑↓←→", -1)),
                        _createTextVNode(" " + _toDisplayString(_unref(t)('patient.imageViewer.pan')), 1)
                      ]),
                      _createElementVNode("span", _hoisted_33, [
                        _cache[29] || (_cache[29] = _createElementVNode("kbd", null, "Esc", -1)),
                        _createTextVNode(" " + _toDisplayString(_unref(t)('patient.imageViewer.closeShort')), 1)
                      ])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_34, [
                    _createElementVNode("button", {
                      class: "close-btn",
                      onClick: close,
                      title: _unref(t)('patient.imageViewer.close')
                    }, [
                      _cache[30] || (_cache[30] = _createElementVNode("svg", {
                        width: "18",
                        height: "18",
                        viewBox: "0 0 24 24",
                        fill: "none",
                        stroke: "currentColor",
                        "stroke-width": "2"
                      }, [
                        _createElementVNode("line", {
                          x1: "18",
                          y1: "6",
                          x2: "6",
                          y2: "18"
                        }),
                        _createElementVNode("line", {
                          x1: "6",
                          y1: "6",
                          x2: "18",
                          y2: "18"
                        })
                      ], -1)),
                      _createElementVNode("span", _hoisted_36, _toDisplayString(_unref(t)('patient.imageViewer.closeShort')), 1)
                    ], 8, _hoisted_35)
                  ])
                ])
              ], 2)
            ], 512))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})